import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  CloudArrowDownIcon,
  ArrowPathIcon,
  TableCellsIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { useAuth } from 'connections/AuthContext';
import { fetchFlowAccounts, createManyFlowPreliminaryTransactions } from 'views/finance/moduleServices/flow_requests';
import { getPluggyTransactions } from 'views/finance/moduleServices/pluggy_services';
import {
  downloadPreviewAccountFlows,
  flowTypeFromPreviewType,
} from '../../viewServices/UpdateCustomAccountsDownloadConfig';
import {
  PREVIEW_FLOW_COLUMNS,
  pluggyTransactionToPreviewRow,
  convertJsonTextToPreviewRows,
  parseExcelToPreviewRows,
} from '../../viewServices/preview_flow_schema';
import {
  makePreviewImportJsonKey,
  makePreviewImportExcelKey,
  removePreviewEntriesMatchingAccountName,
} from '../../viewServices/preview_map_utils';
import {
  loadDownloadPreviewSnapshot,
  saveDownloadPreviewSnapshot,
} from '../../viewServices/preview_download_local_storage';
import {
  bakePreviewOpenfinanceData,
  bakePreviewTransactions,
} from '../../viewServices/preview_row_bake';
import { buildPreliminaryPayloadsFromPreviewMap } from '../../viewServices/preview_merge_preliminary';
import { useFinanceProfile } from 'views/finance/moduleServices/finance_profilecontext';
import DropdownCustom from 'views/finance/moduleComponents/DropdownCustom';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';
import DatePicker from 'shared_items/DatePicker';

const FETCH_TABS = [
  { id: 'pluggy', label: 'Pluggy API' },
  { id: 'json', label: 'JSON field' },
  { id: 'excel', label: 'Excel file' },
];

const tabButtonClass = (active) =>
  `px-3 py-1 text-xs rounded ${
    active ? 'bg-green-800 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
  }`;

const PLUGGY_GMT_OPTIONS = [
  { value: '-5', label: 'GMT-5' },
  { value: '-4', label: 'GMT-4' },
  { value: '-3', label: 'GMT-3 (Brasília)' },
  { value: '-2', label: 'GMT-2' },
  { value: '0', label: 'GMT+0 (UTC)' },
  { value: '1', label: 'GMT+1' },
  { value: '2', label: 'GMT+2' },
  { value: '3', label: 'GMT+3' },
];

// ##### UPDATE BATCH CARD - ACCOUNTS DOWNLOAD
const CustomAccountsDownload = forwardRef(({ reload, onPreliminaryMerged }, ref) => {
  const { user } = useAuth();
  const { defaultCurrency, profile } = useFinanceProfile();
  const storageUserId =
    user?.id != null && String(user.id).trim() !== '' ? String(user.id) : 'anon';

  const [accounts, setAccounts] = useState([]);
  /** { [accountId]: { account, transactions: canonical[], error? } } */
  const [openfinanceData, setOpenfinanceData] = useState({});
  const [loading, setLoading] = useState({
    fetching: false,
    initialData: false,
    importJson: false,
    importExcel: false,
    mergePreliminary: false,
  });
  const [selectedTab, setSelectedTab] = useState(null);
  const [fetchMethodTab, setFetchMethodTab] = useState('pluggy');

  const [dateRange, setDateRange] = useState({
    from_date: '',
    to_date: '',
    page_size: '',
  });
  const [timezoneOffset, setTimezoneOffset] = useState(-3);
  const [exchangeRates, setExchangeRates] = useState({});
  /** Merge-only filter; not persisted. Both empty = no trim. */
  const [trimMergeRange, setTrimMergeRange] = useState({ start: '', end: '' });

  const [importAccountNameJson, setImportAccountNameJson] = useState('Imported (JSON)');
  const [importAccountNameExcel, setImportAccountNameExcel] = useState('Imported (Excel)');
  const [jsonFieldText, setJsonFieldText] = useState('');
  const [jsonImportMode, setJsonImportMode] = useState('flash');
  const [excelFile, setExcelFile] = useState(null);
  const [excelSheetName, setExcelSheetName] = useState('data');
  const [excelHeaderRow, setExcelHeaderRow] = useState('1');
  /** Last chosen file name only (persisted); user must re-select file after reload. */
  const [excelFileName, setExcelFileName] = useState('');
  const [excelDragOver, setExcelDragOver] = useState(false);
  const [previewStorageHydrated, setPreviewStorageHydrated] = useState(false);
  const excelFileInputRef = useRef(null);
  /** Portal anchor so GMT menu is not clipped by accordion overflow:hidden */
  const pluggyGmtDropdownRef = useRef(null);

  const [mergePreliminaryModalOpen, setMergePreliminaryModalOpen] = useState(false);
  const [pendingMergePayloads, setPendingMergePayloads] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const snap = loadDownloadPreviewSnapshot(storageUserId);
    if (snap) {
      const tzSnap =
        snap.timezoneOffset != null && !Number.isNaN(Number(snap.timezoneOffset))
          ? Number(snap.timezoneOffset)
          : -3;
      setOpenfinanceData(bakePreviewOpenfinanceData(snap.openfinanceData || {}, tzSnap));
      if (snap.selectedTab != null) setSelectedTab(snap.selectedTab);
      if (snap.fetchMethodTab && FETCH_TABS.some((t) => t.id === snap.fetchMethodTab)) {
        setFetchMethodTab(snap.fetchMethodTab);
      }
      if (snap.dateRange && typeof snap.dateRange === 'object') {
        setDateRange((prev) => ({ ...prev, ...snap.dateRange }));
      }
      if (snap.timezoneOffset != null && !Number.isNaN(Number(snap.timezoneOffset))) {
        setTimezoneOffset(Number(snap.timezoneOffset));
      }
      if (snap.exchangeRates && typeof snap.exchangeRates === 'object') {
        setExchangeRates(snap.exchangeRates);
      }
      if (typeof snap.importAccountNameJson === 'string') setImportAccountNameJson(snap.importAccountNameJson);
      if (typeof snap.jsonFieldText === 'string') setJsonFieldText(snap.jsonFieldText);
      if (snap.jsonImportMode === 'flash' || snap.jsonImportMode === 'simple') {
        setJsonImportMode(snap.jsonImportMode);
      }
      if (typeof snap.importAccountNameExcel === 'string') {
        setImportAccountNameExcel(snap.importAccountNameExcel);
      }
      if (typeof snap.excelSheetName === 'string') setExcelSheetName(snap.excelSheetName);
      if (snap.excelHeaderRow != null) setExcelHeaderRow(String(snap.excelHeaderRow));
      if (typeof snap.excelFileName === 'string') setExcelFileName(snap.excelFileName);
    }
    setPreviewStorageHydrated(true);
  }, [storageUserId]);

  useEffect(() => {
    const keys = Object.keys(openfinanceData);
    if (keys.length === 0) {
      if (selectedTab != null) setSelectedTab(null);
      return;
    }
    if (selectedTab == null || openfinanceData[selectedTab] === undefined) {
      setSelectedTab(keys[0]);
    }
  }, [openfinanceData, selectedTab]);

  useEffect(() => {
    if (!previewStorageHydrated) return;
    const t = window.setTimeout(() => {
      try {
        saveDownloadPreviewSnapshot(storageUserId, {
          openfinanceData,
          selectedTab,
          fetchMethodTab,
          dateRange,
          timezoneOffset,
          exchangeRates,
          importAccountNameJson,
          jsonFieldText,
          jsonImportMode,
          importAccountNameExcel,
          excelSheetName,
          excelHeaderRow,
          excelFileName,
        });
      } catch (e) {
        const name = e?.name;
        const code = e?.code;
        if (name === 'QuotaExceededError' || code === 22) {
          toast.error('Browser storage is full; preview state was not saved.');
        }
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [
    previewStorageHydrated,
    storageUserId,
    openfinanceData,
    selectedTab,
    fetchMethodTab,
    dateRange,
    timezoneOffset,
    exchangeRates,
    importAccountNameJson,
    jsonFieldText,
    jsonImportMode,
    importAccountNameExcel,
    excelSheetName,
    excelHeaderRow,
    excelFileName,
  ]);

  useImperativeHandle(ref, () => ({
    refreshData: loadInitialData,
  }));

  const loadInitialData = async () => {
    setLoading((prev) => ({ ...prev, initialData: true }));
    try {
      const accountsData = await fetchFlowAccounts();
      setAccounts(accountsData || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load initial data');
    } finally {
      setLoading((prev) => ({ ...prev, initialData: false }));
    }
  };

  const getMappedAccounts = () =>
    accounts.filter((acc) => acc.openfinance_item && acc.openfinance_account);

  const handleFetchAllData = async () => {
    const mappedAccounts = getMappedAccounts();

    if (mappedAccounts.length === 0) {
      toast.error('No accounts with OpenFinance mapping found');
      return;
    }

    setLoading((prev) => ({ ...prev, fetching: true }));

    try {
      const fetchPromises = mappedAccounts.map(async (account) => {
        try {
          const transactions = await getPluggyTransactions({
            account_id: account.openfinance_account,
            page_size: dateRange.page_size,
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          });
          const raw = transactions || [];
          const canonical = raw
            .map((tx) => pluggyTransactionToPreviewRow(tx))
            .filter(Boolean);
          const baked = bakePreviewTransactions(canonical, timezoneOffset);
          return { account, transactions: baked, error: null };
        } catch (error) {
          console.error(`Error fetching data for account ${account.account_name}:`, error);
          return { account, transactions: [], error: error.message };
        }
      });

      const results = await Promise.all(fetchPromises);

      setOpenfinanceData((prev) => {
        let next = { ...prev };
        results.forEach(({ account, transactions, error }) => {
          next = removePreviewEntriesMatchingAccountName(next, account.account_name);
          next[String(account.id)] = {
            account,
            transactions,
            error,
          };
        });
        return next;
      });

      setSelectedTab(String(mappedAccounts[0].id));

      const totalRows = results.reduce((sum, r) => sum + (r.transactions?.length || 0), 0);
      const errorCount = results.filter((r) => r.error).length;
      if (errorCount > 0) {
        toast.error(`Fetch failed for ${errorCount} account(s). Check mapping and Pluggy connection.`);
      } else if (totalRows === 0) {
        toast.error(
          'Pluggy returned 0 transactions. Accounts may be stale — refresh Pluggy cache in Manage Accounts and verify mappings.',
          { duration: 7000 }
        );
      } else {
        toast.success(`Fetched ${totalRows} transaction(s) from ${mappedAccounts.length} account(s)`);
      }
    } catch (error) {
      console.error('Error fetching all data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading((prev) => ({ ...prev, fetching: false }));
    }
  };

  const handleLoadJsonPreview = () => {
    setLoading((prev) => ({ ...prev, importJson: true }));
    window.setTimeout(() => {
      try {
        const rows = convertJsonTextToPreviewRows(jsonFieldText, { mode: jsonImportMode });
        const baked = bakePreviewTransactions(rows, timezoneOffset);
        const name = importAccountNameJson.trim() || 'Imported (JSON)';
        const previewKey = makePreviewImportJsonKey(name);
        setOpenfinanceData((prev) => {
          const next = removePreviewEntriesMatchingAccountName(prev, name);
          next[previewKey] = {
            account: { id: previewKey, account_name: name },
            transactions: baked,
            error: null,
          };
          return next;
        });
        setSelectedTab(previewKey);
        toast.success(`Preview loaded: ${rows.length} row(s)`);
      } catch (e) {
        toast.error(e.message || 'Failed to parse JSON');
      } finally {
        setLoading((prev) => ({ ...prev, importJson: false }));
      }
    }, 0);
  };

  const handleReadExcelPreview = async () => {
    if (!excelFile) {
      toast.error('Select or drop an Excel file first');
      return;
    }
    const headerRowNum = Math.max(1, parseInt(excelHeaderRow, 10) || 1);
    setLoading((prev) => ({ ...prev, importExcel: true }));
    try {
      const buf = await excelFile.arrayBuffer();
      const rows = parseExcelToPreviewRows(buf, {
        sheetName: excelSheetName.trim() || 'data',
        headerRow: headerRowNum,
      });
      const baked = bakePreviewTransactions(rows, timezoneOffset);
      const name = importAccountNameExcel.trim() || 'Imported (Excel)';
      const previewKey = makePreviewImportExcelKey(name);
      setOpenfinanceData((prev) => {
        const next = removePreviewEntriesMatchingAccountName(prev, name);
        next[previewKey] = {
          account: { id: previewKey, account_name: name },
          transactions: baked,
          error: null,
        };
        return next;
      });
      setSelectedTab(previewKey);
      toast.success(`Preview loaded: ${rows.length} row(s)`);
    } catch (e) {
      toast.error(e.message || 'Failed to read Excel');
    } finally {
      setLoading((prev) => ({ ...prev, importExcel: false }));
    }
  };

  const handleDownloadExcel = () => {
    downloadPreviewAccountFlows(
      openfinanceData,
      exchangeRates,
      defaultCurrency,
      profile?.default_categ_l0
    );
  };

  const handleMergeToPreliminary = () => {
    const categL0 = (profile?.default_categ_l0 || '').trim();
    if (!categL0) {
      toast.error('Set default category L0 in your finance profile first');
      return;
    }

    const tStart = (trimMergeRange.start || '').trim();
    const tEnd = (trimMergeRange.end || '').trim();
    if (tStart && tEnd && tStart > tEnd) {
      toast.error('Invalid date range: start is after end');
      return;
    }

    const { payloads, errors } = buildPreliminaryPayloadsFromPreviewMap(openfinanceData, {
      defaultCategL0: categL0,
      exchangeRates,
      defaultCurrency,
      trimMergeStart: tStart,
      trimMergeEnd: tEnd,
    });

    if (errors.length > 0) {
      toast.error(
        errors.slice(0, 4).join(' · ') + (errors.length > 4 ? ` (+${errors.length - 4} more)` : '')
      );
      return;
    }
    if (payloads.length === 0) {
      toast.error(
        tStart && tEnd
          ? 'No preview rows fall in the selected date range'
          : 'No preview rows to merge (load data in all accounts you need)'
      );
      return;
    }

    setPendingMergePayloads(payloads);
    setMergePreliminaryModalOpen(true);
  };

  const handleCancelMergePreliminary = () => {
    setMergePreliminaryModalOpen(false);
    setPendingMergePayloads(null);
  };

  const handleConfirmMergePreliminary = async () => {
    const payloads = pendingMergePayloads;
    if (!payloads?.length) {
      handleCancelMergePreliminary();
      return;
    }
    const toSend = payloads;
    setMergePreliminaryModalOpen(false);
    setPendingMergePayloads(null);

    setLoading((prev) => ({ ...prev, mergePreliminary: true }));
    try {
      const res = await createManyFlowPreliminaryTransactions(toSend);
      const n = res?.created ?? 0;
      if (n === 0) {
        toast(res?.detail || 'No new rows created (all skipped as duplicates)');
      } else {
        toast.success(res?.detail || `${n} record(s) created in Preliminary`);
      }
      onPreliminaryMerged?.();
    } catch (e) {
      const d = e.response?.data;
      const msgErr =
        (typeof d === 'string' ? d : d?.detail || d?.error) ||
        e.message ||
        'Merge to Preliminary failed';
      toast.error(msgErr);
    } finally {
      setLoading((prev) => ({ ...prev, mergePreliminary: false }));
    }
  };

  const getUniqueCurrencies = () => {
    const currencies = new Set();
    Object.values(openfinanceData).forEach((data) => {
      data.transactions?.forEach((row) => {
        if (row.flow_currency) currencies.add(row.flow_currency);
      });
    });
    return Array.from(currencies).sort();
  };

  const handleExchangeRateChange = (currency, rate) => {
    setExchangeRates((prev) => ({
      ...prev,
      [currency]: parseFloat(rate) || 0,
    }));
  };

  const hasAnyPreviewRows = Object.values(openfinanceData).some(
    (d) => d.transactions?.length > 0
  );
  const hasData = Object.keys(openfinanceData).length > 0;
  const canExport = hasAnyPreviewRows;
  const canMergePreliminary =
    hasAnyPreviewRows && !loading.mergePreliminary && !mergePreliminaryModalOpen;

  const removePreviewAccount = useCallback((accountIdKey) => {
    setOpenfinanceData((prev) => {
      const next = { ...prev };
      delete next[accountIdKey];
      return next;
    });
  }, []);

  const pickExcelFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Please use an Excel file (.xlsx or .xls)');
      return;
    }
    setExcelFile(file);
    setExcelFileName(file.name);
  };

  return (
    <div className="min-h-96">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
        {/* Column 1: a. Fetch Data (~40%) */}
        <div className="min-w-0 md:col-span-2 flex flex-col">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-gray-700">a. Fetch Data</h3>
          </div>

          <div className="flex flex-wrap gap-1 mb-3">
            {FETCH_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setFetchMethodTab(t.id)}
                className={tabButtonClass(fetchMethodTab === t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {fetchMethodTab === 'pluggy' && (
            <div className="flex flex-col gap-2.5">
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <div className="flex flex-col gap-0.5 min-w-0 flex-1 basis-[calc(50%-0.375rem)]">
                  <span className="text-[11px] text-gray-500">From</span>
                  <DatePicker
                    name="from_date"
                    value={dateRange.from_date}
                    onChange={(e) => {
                      const v = e?.target?.value;
                      setDateRange((prev) => ({ ...prev, from_date: typeof v === 'string' ? v : '' }));
                    }}
                    mode="single"
                    colorScheme="green"
                    placeholder="From"
                    className="w-full justify-between min-h-[2rem] border border-gray-300 bg-white shadow-sm"
                  />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0 flex-1 basis-[calc(50%-0.375rem)]">
                  <span className="text-[11px] text-gray-500">To</span>
                  <DatePicker
                    name="to_date"
                    value={dateRange.to_date}
                    onChange={(e) => {
                      const v = e?.target?.value;
                      setDateRange((prev) => ({ ...prev, to_date: typeof v === 'string' ? v : '' }));
                    }}
                    mode="single"
                    colorScheme="green"
                    placeholder="To"
                    className="w-full justify-between min-h-[2rem] border border-gray-300 bg-white shadow-sm"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-3">
                <div className="flex flex-col gap-0.5 min-w-0 flex-1 basis-[calc(50%-0.375rem)]">
                  <label htmlFor="fetch-page-size" className="text-[11px] text-gray-500">
                    Limit lines
                  </label>
                  <input
                    id="fetch-page-size"
                    type="number"
                    min="1"
                    placeholder="e.g. 100"
                    value={dateRange.page_size}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, page_size: e.target.value }))}
                    className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
                  />
                </div>
                <div
                  ref={pluggyGmtDropdownRef}
                  className="flex flex-col gap-0.5 min-w-0 flex-1 basis-[calc(50%-0.375rem)]"
                >
                  <span className="text-[11px] text-gray-500">GMT</span>
                  <DropdownCustom
                    options={PLUGGY_GMT_OPTIONS}
                    selectedValue={String(timezoneOffset)}
                    onSelect={(option) => {
                      const raw = typeof option === 'object' && option !== null ? option.value : option;
                      const n = parseInt(String(raw), 10);
                      if (!Number.isNaN(n)) setTimezoneOffset(n);
                    }}
                    placeholder="GMT"
                    width="w-full"
                    usePortal
                    portalRef={pluggyGmtDropdownRef}
                    zIndex="z-[100]"
                  />
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleFetchAllData}
                  disabled={loading.fetching || getMappedAccounts().length === 0}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-green-800 text-white rounded text-xs font-medium hover:bg-green-900 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loading.fetching ? 'animate-spin' : ''}`} />
                  {loading.fetching ? 'Fetching...' : 'Fetch Data'}
                </button>
              </div>
            </div>
          )}

          {fetchMethodTab === 'json' && (
            <div className="flex flex-col gap-2.5">
              <div className="flex flex-wrap items-end gap-2 sm:gap-3">
                <div className="flex flex-col gap-0.5 min-w-0 flex-1 basis-[min(100%,14rem)]">
                  <label htmlFor="import-account-json" className="text-[11px] text-gray-500">
                    Preview account name
                  </label>
                  <input
                    id="import-account-json"
                    type="text"
                    value={importAccountNameJson}
                    onChange={(e) => setImportAccountNameJson(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1 w-full min-w-0"
                    placeholder="Imported (JSON)"
                  />
                </div>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <span className="text-[11px] text-gray-500">JSON type</span>
                  <div
                    className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5 shadow-sm"
                    role="group"
                    aria-label="JSON import type"
                  >
                    <button
                      type="button"
                      onClick={() => setJsonImportMode('flash')}
                      className={`rounded-md px-3 py-1 text-xs font-medium transition-all duration-150 ${
                        jsonImportMode === 'flash'
                          ? 'bg-white text-green-900 shadow-sm ring-1 ring-black/5'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Flash
                    </button>
                    <button
                      type="button"
                      onClick={() => setJsonImportMode('simple')}
                      className={`rounded-md px-3 py-1 text-xs font-medium transition-all duration-150 ${
                        jsonImportMode === 'simple'
                          ? 'bg-white text-green-900 shadow-sm ring-1 ring-black/5'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Simple
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor="json-field-text" className="text-[11px] text-gray-500">
                  JSON
                </label>
                <textarea
                  id="json-field-text"
                  value={jsonFieldText}
                  onChange={(e) => setJsonFieldText(e.target.value)}
                  rows={8}
                  className="text-xs border border-gray-300 rounded px-2 py-1.5 w-full font-mono"
                  placeholder={
                    jsonImportMode === 'flash'
                      ? '[ { "result": { "data": { "json": { "items": [ … ] } } } } ]'
                      : '[ {...}, ... ] or { "transactions": [ ... ] }'
                  }
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleLoadJsonPreview}
                  disabled={loading.importJson}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-green-800 text-white rounded text-xs font-medium hover:bg-green-900 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loading.importJson ? 'animate-spin' : ''}`} />
                  {loading.importJson ? 'Loading...' : 'Load Data'}
                </button>
              </div>
            </div>
          )}

          {fetchMethodTab === 'excel' && (
            <div className="flex flex-col gap-2.5">
              <div className="flex flex-col gap-0.5">
                <label htmlFor="import-account-excel" className="text-[11px] text-gray-500">
                  Preview account name
                </label>
                <input
                  id="import-account-excel"
                  type="text"
                  value={importAccountNameExcel}
                  onChange={(e) => setImportAccountNameExcel(e.target.value)}
                  className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
                  placeholder="Imported (Excel)"
                />
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <label htmlFor="excel-sheet-name" className="text-[11px] text-gray-500">
                    Sheet name
                  </label>
                  <input
                    id="excel-sheet-name"
                    type="text"
                    value={excelSheetName}
                    onChange={(e) => setExcelSheetName(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
                    placeholder="data"
                  />
                </div>
                <div className="flex flex-col gap-0.5 w-24 shrink-0">
                  <label htmlFor="excel-header-row" className="text-[11px] text-gray-500">
                    Header row
                  </label>
                  <input
                    id="excel-header-row"
                    type="number"
                    min="1"
                    value={excelHeaderRow}
                    onChange={(e) => setExcelHeaderRow(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
                  />
                </div>
              </div>

              <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && excelFileInputRef.current?.click()}
                onClick={() => excelFileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExcelDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExcelDragOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExcelDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  pickExcelFile(f);
                }}
                className={`min-h-24 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-3 text-center cursor-pointer transition-colors ${
                  excelDragOver
                    ? 'border-green-600 bg-green-50'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-green-500'
                }`}
              >
                <input
                  ref={excelFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => pickExcelFile(e.target.files?.[0])}
                />
                <span className="text-sm font-medium text-gray-600 px-1">
                  {excelDragOver ? 'Drop file here' : 'Select or drag Excel file here'}
                </span>
                {(excelFile || excelFileName) && (
                  <p
                    className="text-xs text-gray-700 truncate max-w-full px-1"
                    title={excelFile?.name || excelFileName}
                  >
                    {excelFile?.name || excelFileName}
                    {!excelFile && excelFileName ? ' (re-select file to load)' : ''}
                  </p>
                )}
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleReadExcelPreview}
                  disabled={loading.importExcel}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-green-800 text-white rounded text-xs font-medium hover:bg-green-900 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loading.importExcel ? 'animate-spin' : ''}`} />
                  {loading.importExcel ? 'Reading...' : 'Read Data'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Column 2: b. Preview + c. Save or Export (~60%) */}
        <div className="min-w-0 md:col-span-3 flex flex-col gap-4">
          {/* b. Preview */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">b. Preview</h3>

            {!hasData ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <TableCellsIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600">Fetch or load data to continue</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-1 mb-3">
                  {Object.entries(openfinanceData).map(([accountId, data]) => {
                    const active = selectedTab === accountId;
                    return (
                      <div
                        key={accountId}
                        className={`inline-flex items-stretch rounded-md overflow-hidden border ${
                          active ? 'border-green-800' : 'border-gray-300'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setSelectedTab(accountId)}
                          className={`px-3 py-1 text-xs ${
                            active
                              ? 'bg-green-800 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {data.account.account_name}
                          {data.error && <span className="text-red-300 ml-1">⚠</span>}
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove ${data.account.account_name} from preview`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removePreviewAccount(accountId);
                          }}
                          className={`px-1.5 flex items-center justify-center border-l text-xs shrink-0 ${
                            active
                              ? 'border-green-700 bg-green-800 text-white hover:bg-green-900'
                              : 'border-gray-300 bg-gray-200 text-gray-600 hover:bg-red-100 hover:text-red-800'
                          }`}
                        >
                          <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {selectedTab && openfinanceData[selectedTab] && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {openfinanceData[selectedTab].error ? (
                      <div className="p-4 text-center text-red-600 text-sm">
                        Error: {openfinanceData[selectedTab].error}
                      </div>
                    ) : (
                      <div className="overflow-auto max-h-[300px]">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0 z-5">
                            <tr>
                              {PREVIEW_FLOW_COLUMNS.map((col) => (
                                <th
                                  key={col}
                                  className="px-2 py-1 text-left text-xs font-semibold text-gray-600 border-b"
                                >
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {openfinanceData[selectedTab].transactions.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={PREVIEW_FLOW_COLUMNS.length}
                                  className="px-2 py-6 text-center text-gray-500"
                                >
                                  No rows for this account
                                </td>
                              </tr>
                            ) : (
                              openfinanceData[selectedTab].transactions.map((row, index) => {
                                const dateStr =
                                  row.date && String(row.date).trim()
                                    ? String(row.date).trim()
                                    : 'none';
                                const hourStr =
                                  row.hour && String(row.hour).trim()
                                    ? String(row.hour).trim()
                                    : 'none';
                                const ft =
                                  row.flow_type === 'In' || row.flow_type === 'Out'
                                    ? row.flow_type
                                    : flowTypeFromPreviewType(row.raw_type);
                                const typeDisplay =
                                  ft === 'In' || ft === 'Out' ? ft : row.raw_type || 'none';
                                const typeBadgeClass =
                                  ft === 'In'
                                    ? 'bg-green-100 text-green-800'
                                    : ft === 'Out'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-700';

                                return (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-2 py-1 text-xs text-gray-700 whitespace-nowrap">
                                      {dateStr}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">{hourStr}</td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.desc || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.raw_type || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-center">
                                      <span className={`px-2 py-1 rounded ${typeBadgeClass}`}>
                                        {typeDisplay}
                                      </span>
                                    </td>
                                    <td className="px-2 py-1 text-xs text-right font-medium">
                                      {row.flow_value !== '' && row.flow_value != null
                                        ? row.flow_value
                                        : 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.flow_currency || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.extras_description || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.categ_l1 || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.extras_id || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.status || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700 whitespace-nowrap">
                                      {row.external_id || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.extras_cardnumber || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.payee_mcc || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.purchase_date || 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.total_installments ?? 'none'}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-700">
                                      {row.installment_number ?? 'none'}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* c. Save or Export */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">c. Save or Export</h3>

            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-3 sm:gap-y-1">
              <span className="text-xs text-gray-600 shrink-0">Trim dates to merge:</span>
              <DatePicker
                name="trim_merge_range"
                mode="range"
                value={trimMergeRange}
                onChange={(e) => {
                  const v = e?.target?.value;
                  if (v && typeof v === 'object') {
                    setTrimMergeRange({
                      start: typeof v.start === 'string' ? v.start : '',
                      end: typeof v.end === 'string' ? v.end : '',
                    });
                  }
                }}
                colorScheme="green"
                placeholder="All dates"
                className="w-full sm:w-auto min-w-[12rem] justify-between min-h-[2rem] border border-gray-300 bg-white shadow-sm"
              />
            </div>

            <p className="text-xs text-gray-600 mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span>
                Default Currency: <span className="font-medium text-gray-800">{defaultCurrency}</span>
              </span>
              <span className="text-gray-300 select-none" aria-hidden>
                |
              </span>
              <span>
                Exchange Rates to <span className="font-medium text-gray-800">{defaultCurrency}</span>:
              </span>
            </p>

            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="min-w-0 flex-1">
                {hasAnyPreviewRows && getUniqueCurrencies().length > 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Currency</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Exchange Rate</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Example (1 unit)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {getUniqueCurrencies().map((currency) => {
                          const rate = exchangeRates[currency] || 0;
                          const example = (1 * rate).toFixed(2);
                          return (
                            <tr key={currency}>
                              <td className="px-2 py-1.5 font-medium text-gray-800">{currency}</td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="number"
                                  step="0.0001"
                                  min="0"
                                  placeholder="0.0000"
                                  value={rate || ''}
                                  onChange={(e) => handleExchangeRateChange(currency, e.target.value)}
                                  className="w-20 text-xs border border-gray-300 rounded px-2 py-0.5"
                                />
                              </td>
                              <td className="px-2 py-1.5 text-gray-600">
                                {example} {defaultCurrency}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>

              <div className="shrink-0 sm:pt-0.5 sm:self-start flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleMergeToPreliminary}
                  disabled={!canMergePreliminary}
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                    canMergePreliminary
                      ? 'bg-amber-600 text-white hover:bg-amber-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title="Send all preview rows from every loaded account to Flow Preliminary (uses exchange rates and default L0 in this section)"
                >
                  <ClipboardDocumentListIcon className="h-4 w-4 shrink-0" />
                  {loading.mergePreliminary ? 'Merging…' : 'Merge to Preliminary'}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadExcel}
                  disabled={!canExport}
                  className={`flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                    canExport
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  <CloudArrowDownIcon className="h-4 w-4 shrink-0" />
                  Download Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalConfirm
        isOpen={mergePreliminaryModalOpen}
        onClose={handleCancelMergePreliminary}
        onConfirm={handleConfirmMergePreliminary}
        title="Merge to Preliminary"
        message={
          pendingMergePayloads?.length
            ? `Send ${pendingMergePayloads.length} row(s) from all loaded preview accounts to Flow Preliminary? Rows whose external_id is already saved for your user are skipped.`
            : ''
        }
        confirmText="Merge"
        cancelText="Cancel"
        confirmButtonClass="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white shadow"
        cancelButtonClass="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
      />
    </div>
  );
});

export default CustomAccountsDownload;
